// A promise race that rejects if `promise` doesn't settle within `ms`. Mirrors
// core/connect-timeout.ts's watchdog shape — kept pure + DOM-free so the timing
// is unit-testable with fake timers.
//
// AnchorConversation.load() wraps its history fetch in this (CYBORG-794): a
// dropped RPC response (relay reconnect / transient) fires neither resolve nor
// reject, so the bare `await fetchPage(...)` never settled and pinned `loading`
// on TRUE forever — an eternal loading skeleton with no recovery. A timeout now
// rejects, which the component treats exactly like a fetch failure (the Retry
// state), which in turn flips loading→false and re-arms the connect-recovery
// refetch.
//
// Settles with the underlying promise's own value/reason when it wins the race;
// the timer is always cleared (win or lose) so a slow real response leaks none.
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const watchdog = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Timed out after ${ms}ms`)), ms);
  });
  return Promise.race([promise, watchdog]).finally(() => clearTimeout(timer));
}
